// client/src/pages/InstallApp.js (CLIENT – Cortex PWA install helper)

import React, { useEffect, useRef, useState } from "react";
import "./Dashboard.css";
import "./InstallApp.css";

import CphereHeaderLogo from "../components/CphereHeaderLogo";
import appleImage from "../assets/apple.jpg";
import androidImage from "../assets/android.jpg";
import { useNavigate } from "react-router-dom";

const InstallApp = () => {
  const navigate = useNavigate();
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const closeButtonRef = useRef(null);

  const platforms = [
    {
      key: "ios",
      title: "Apple iOS",
      summary: "Add Cortex to your iPhone or iPad home screen for a true native app feel.",
      helper: "Best installed from Safari",
      image: appleImage,
      steps: [
        <>
          Open <strong>analytics.cphere.ai</strong> in Safari on your iPhone or iPad.
        </>,
        <>
          Tap the <strong>Share</strong> icon.
        </>,
        <>
          Select <strong>Add to Home Screen</strong>.
        </>,
        <>
          Tap <strong>Add</strong>.
        </>,
      ],
    },
    {
      key: "android",
      title: "Android",
      summary: "Install Cortex as a PWA on your Android device for a true native app feel.",
      helper: "Best installed from Chrome or Brave",
      image: androidImage,
      steps: [
        <>
          Open <strong>analytics.cphere.ai</strong> in Chrome or Brave.
        </>,
        <>
          Tap the <strong>⋮</strong> menu.
        </>,
        <>
          Select <strong>Add to Home screen</strong> or <strong>Install</strong>.
        </>,
        <>
          Tap <strong>Install</strong>.
        </>,
      ],
    },
  ];

  const activePlatform =
    platforms.find((platform) => platform.key === selectedPlatform) || null;

  useEffect(() => {
    if (!activePlatform) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [activePlatform]);

  useEffect(() => {
    if (!activePlatform) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setSelectedPlatform(null);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [activePlatform]);

  useEffect(() => {
    if (!activePlatform) {
      return;
    }

    closeButtonRef.current?.focus();
  }, [activePlatform]);

  const handleLogoClick = () => {
    navigate("/dashboard");
  };

  const handleModalClose = () => {
    setSelectedPlatform(null);
  };

  return (
    <div className="installapp-page-container">
      <div className="installapp-page-content">
        <CphereHeaderLogo
          alt="Powered by Cphere"
          className="installapp-poweredby-logo"
          onClick={handleLogoClick}
        />

        <div className="installapp-hero-container">
          <div className="installapp-hero-pill">
            <span className="installapp-hero-pill-label cphere-hero-pill-wordmark">Install</span>
            <span className="installapp-hero-pill-tag cphere-hero-pill-tag">App</span>
          </div>
          <div className="installapp-hero-shimmer-bar" />
        </div>

        <div className="installapp-selector-grid">
          {platforms.map((platform) => {
            const isActive = platform.key === activePlatform?.key;

            return (
              <button
                key={platform.key}
                type="button"
                className={`installapp-selector-card ${isActive ? "is-active" : ""}`}
                onClick={() => setSelectedPlatform(platform.key)}
                aria-pressed={isActive}
              >
                <div className="installapp-hero-badge-wrapper">
                  <img
                    src={platform.image}
                    alt={platform.title}
                    className="installapp-hero-img"
                  />
                </div>

                <div className="installapp-selector-content">
                  <div className="installapp-selector-topline">
                    <h2 className="installapp-card-title">{platform.title}</h2>
                    <span className="installapp-selector-state">
                      {isActive ? "Selected" : "Choose"}
                    </span>
                  </div>

                  <p className="installapp-card-text">{platform.summary}</p>
                  <div className="installapp-selector-helper">{platform.helper}</div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {activePlatform ? (
        <div
          className="installapp-modal-overlay"
          role="presentation"
          onClick={handleModalClose}
        >
          <div
            className="installapp-modal-shell"
            role="dialog"
            aria-modal="true"
            aria-labelledby="installapp-modal-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="installapp-modal-panel">
              <button
                ref={closeButtonRef}
                type="button"
                className="installapp-modal-close"
                onClick={handleModalClose}
                aria-label="Close install instructions"
              >
                CLOSE
              </button>

              <div className="installapp-modal-content">
                <section className="installapp-instruction-panel installapp-instruction-panel-modal">
                  <div className="installapp-instruction-panel-header">
                    <div className="installapp-hero-badge-wrapper installapp-panel-badge">
                      <img
                        src={activePlatform.image}
                        alt={activePlatform.title}
                        className="installapp-hero-img"
                      />
                    </div>

                    <div className="installapp-instruction-copy">
                      <div className="installapp-instruction-eyebrow">Selected Platform</div>
                      <h2
                        id="installapp-modal-title"
                        className="installapp-card-title installapp-panel-title"
                      >
                        {activePlatform.title}
                      </h2>
                      <p className="installapp-card-text installapp-panel-text">
                        {activePlatform.summary}
                      </p>
                      <div className="installapp-selector-helper installapp-modal-helper">
                        {activePlatform.helper}
                      </div>
                    </div>
                  </div>

                  <div className="installapp-instructions">
                    <ol className="installapp-steps-list">
                      {activePlatform.steps.map((step, index) => (
                        <li key={`${activePlatform.key}-${index}`}>{step}</li>
                      ))}
                    </ol>
                  </div>
                </section>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default InstallApp;
